import Config from '../config'
import jwtDecode from 'jwt-decode'
import {camelCase as camelCaseObject} from 'change-case-object'
import {encodeAsFormUrlencoded} from '../util/api'
import url from 'url'
import {fetch} from 'fetch-plus'
import {logMiddleware, headerMiddleware} from '../util/request/middleware'
import FetchError from '../error/fetch-error'

import {createFetchClientWithAuth} from '../util/request/middleware'


const membershipClient = createFetchClientWithAuth(url.resolve(Config.IDENTITYSERVER_ENDPOINT, '/membership/api/v1'))

/**
 * Makes a request against the identity server token endpoint
 * @param body
 * @returns {Promise<TResult>}
 * @private
 */
function _tokenEndpointRequest(body){
	return fetch(
		url.resolve(Config.IDENTITYSERVER_ENDPOINT, 'identity/connect/token'),
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded'
			},
			body: encodeAsFormUrlencoded(Object.assign({client_id: Config.CLIENT_ID, client_secret: Config.CLIENT_SECRET}, body))
		},
		[headerMiddleware, logMiddleware]
	)
		.then(_createLoginResponse, _createLoginResponse)
}

/**
 * Logs the user into identifyServer
 * @param username
 * @param password
 * @param deviceId
 * @param passwordAsPin - whether the password field represents a pin
 * @param captcha - optional, when set the captcha will be verified against the google recaptcha api and the account lockout will be bypassed
 * @returns {Promise.<TResult>}
 */
export function login(username, password, deviceId, passwordAsPin, captcha){
	let request = {
		username: username,
		password: password,
		grant_type: 'password',
		scope: 'api offline_access openid',
		acr_values: `deviceId:${deviceId}${passwordAsPin ? ' usePin:kunicorns' : '' }`
	}

	if(captcha){
		request['g-recaptcha-response'] = captcha
	}

	return _tokenEndpointRequest(request)
}

/**
 * Requests a token from identity server for the staff member
 * @param code
 * @param callbackUrl
 * @returns {Promise.<TResult>}
 */
export function loginStaff(code, callbackUrl){
	let request = {
		client_id: 'ro.web',
		grant_type: 'authorization_code',
		code: code,
		scope: 'api offline_access openid',
		redirect_uri: callbackUrl
	}

	return _tokenEndpointRequest(request)
}


/**
 * Creates the login response
 * @param identityResponse
 * @returns {*}
 * @private
 */
function _createLoginResponse(response){
	if(response.status >= 200 && response.status < 500){
		return response.json()
			.then((jsonResponse) => {
				if(response.ok){
					return {
						token: {
							...camelCaseObject(jwtDecode(jsonResponse.access_token)),
							accessToken: jsonResponse.access_token,
							expires: jsonResponse.expires_in,
							refreshToken: jsonResponse.refresh_token
						}
					}
				}else{
					return {
						error: jsonResponse.error_description || jsonResponse.ErrorMessage,
						token: undefined
					}
				}
			})
	}else{
		throw new FetchError(response)
	}
}


/**
 * Refreshes the API Token
 * @param token
 * @returns {Promise<TResult>}
 */
export function refresh(token){
	return _tokenEndpointRequest({
		grant_type: 'refresh_token',
		scope: 'api offline_access',
		refresh_token: token.refreshToken
	})
}

export function logout(token){
	return fetch(url.resolve(Config.IDENTITYSERVER_ENDPOINT, 'identity/connect/revocation'), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded'
		},
		body: encodeAsFormUrlencoded({
			client_id: Config.CLIENT_ID,
			client_secret: Config.CLIENT_SECRET,
			token: token.accessToken,
			id_token_hint: 'access_token'
		})
	})
}

/**
 * Sets up secondary authentication, PIN or Fingerprint
 * type can be the following:
 * - pin
 * - fingerprint
 *
 * @param code
 * @param deviceID
 * @param type
 * @return {*}
 */
export async function createSecondaryAuthAccess(code, deviceID, type="pin"){
	return await membershipClient.post('access', {
		body: {
			"SecurityCodeType": type,
			"DeviceId": deviceID,
			"SecurityCode": code
		}
	})
}

/**
 * Removes secondary auth from the account
 * @param deviceID
 * @return {*}
 */
export async function removeSecondaryAuthAccess(deviceID, type="pin"){
	return await membershipClient.del('access', {
		body: {
			"DeviceId": deviceID,
			"SecurityCodeType": type
		}
	})
}



// WEBPACK FOOTER //
// ./node_modules/mb-app-common/lib/api/login.js